import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { AthleteProvider } from "@/context/AthleteContext";
import { DemoProvider } from "@/context/DemoContext";
import BetaBanner from "@/components/BetaBanner";
import DemoBar from "@/components/DemoBar";

// Public Pages
import SaaSLandingPage from "@/pages/SaaSLandingPage";
import CoachSignupPage from "@/pages/CoachSignupPage";
import InviteSignupPage from "@/pages/InviteSignupPage";
import AthleteSignupPage from "@/pages/AthleteSignupPage";
import ClubSignupPage from "@/pages/ClubSignupPage";
import ClubPilotPage from "@/pages/ClubPilotPage";
import GetStartedPage from "@/pages/GetStartedPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import DemoLandingPage from "@/pages/DemoLandingPage";
import { PaymentSuccess, PaymentCancel, MockPaymentPage } from "@/pages/PaymentPage";
import CalendarPage from "@/pages/CalendarPage";

// Coach Dashboard
import CoachLayout from "@/layouts/CoachLayout";
import CoachDashboard from "@/pages/coach/CoachDashboard";
import CoachAthletes from "@/pages/coach/CoachAthletes";
import CoachAthleteDetail from "@/pages/coach/CoachAthleteDetail";
import AthleteEvaluationPage from "@/pages/coach/AthleteEvaluationPage";
import CoachSessions from "@/pages/coach/CoachSessions";
import CoachDrills from "@/pages/coach/CoachDrills";
import CoachPackages from "@/pages/coach/CoachPackages";
import CoachTrainingPlans from "@/pages/coach/CoachTrainingPlans";
import CoachVideos from "@/pages/coach/CoachVideos";
import CoachFinancials from "@/pages/coach/CoachFinancials";
import CoachSettings from "@/pages/coach/CoachSettings";
import InvitedAthletes from "@/pages/coach/InvitedAthletes";
import AthleteWorkspace from "@/pages/coach/AthleteWorkspace";
import CoachVouchers from "@/pages/coach/CoachVouchers";
import CoachMessages from "@/pages/coach/CoachMessages";
import CoachWeeklyChallenge from "@/pages/coach/CoachWeeklyChallenge";

// Parent/Athlete Dashboard
import ParentLayout from "@/layouts/ParentLayout";
import ParentDashboard from "@/pages/parent/ParentDashboard";
import ParentTechnicalDev from "@/pages/parent/ParentTechnicalDev";
import DevelopmentProfile from "@/pages/parent/DevelopmentProfile";
import ParentVideos from "@/pages/parent/ParentVideos";
import ParentTrainingPlan from "@/pages/parent/ParentTrainingPlan";
import ParentSessions from "@/pages/parent/ParentSessions";
import ParentMonthlyReport from "@/pages/parent/ParentMonthlyReport";
import ParentTeamView from "@/pages/parent/ParentTeamView";
import ParentSettings from "@/pages/parent/ParentSettings";
import PlayerGoals from "@/pages/parent/PlayerGoals";
import ParentMessages from "@/pages/parent/ParentMessages";
import TrainAtHome from "@/pages/parent/TrainAtHome";
import GuidedTraining from "@/pages/parent/GuidedTraining";
import RequireAthletePass from "@/components/RequireAthletePass";

// Platform Admin
import PlatformAdminLayout from "@/layouts/PlatformAdminLayout";
import PlatformAdminDashboard from "@/pages/platform-admin/PlatformAdminDashboard";
import PlatformAdminCoaches from "@/pages/platform-admin/PlatformAdminCoaches";
import PlatformAdminUsers from "@/pages/platform-admin/PlatformAdminUsers";
import PlatformAdminBilling from "@/pages/platform-admin/PlatformAdminBilling";
import PlatformAdminAnalytics from "@/pages/platform-admin/PlatformAdminAnalytics";
import PlatformAdminActivation from "@/pages/platform-admin/PlatformAdminActivation";
import PlatformAdminTransitions from "@/pages/platform-admin/PlatformAdminTransitions";
import PlatformAdminSettings from "@/pages/platform-admin/PlatformAdminSettings";
import PlatformAdminAudit from "@/pages/platform-admin/PlatformAdminAudit";
import PlatformAdminChallenges from "@/pages/platform-admin/PlatformAdminChallenges";
import WeeklyChallenge from "@/pages/parent/WeeklyChallenge";

// Team Workspace (Club/Team Management)
import TeamLayout from "@/layouts/TeamLayout";
import TeamJoinPage from "@/pages/team/TeamJoinPage";
import PlayerJoinPage from "@/pages/team/PlayerJoinPage";
import TeamDashboard from "@/pages/team/TeamDashboard";
import TeamAthletes from "@/pages/team/TeamAthletes";
import TeamAthleteDetail from "@/pages/team/TeamAthleteDetail";
import TeamSessions from "@/pages/team/TeamSessions";
import TeamMessages from "@/pages/team/TeamMessages";
import TeamNotifications from "@/pages/team/TeamNotifications";
import TeamTrainingPlans from "@/pages/team/TeamTrainingPlans";
import TeamDrills from "@/pages/team/TeamDrills";
import TeamVideos from "@/pages/team/TeamVideos";
import TeamMatchReport from "@/pages/team/TeamMatchReport";
import TeamScheduleMatch from "@/pages/team/TeamScheduleMatch";

// Club Admin Panel
import ClubLayout from "@/layouts/ClubLayout";
import ClubDashboard from "@/pages/club/ClubDashboard";
import ClubTeams from "@/pages/club/ClubTeams";
import ClubCoaches from "@/pages/club/ClubCoaches";
import ClubManagers from "@/pages/club/ClubManagers";
import ClubAthletes from "@/pages/club/ClubAthletes";
import ClubSessions from "@/pages/club/ClubSessions";
import ClubInvites from "@/pages/club/ClubInvites";
import ClubSettings from "@/pages/club/ClubSettings";

// Unified Login & Helpdesk
import UnifiedLoginPage from "@/pages/UnifiedLoginPage";
import HelpdeskWidget from "@/components/HelpdeskWidget";
import { InstallPWAPrompt } from "@/lib/pwa";

// Club Intelligence
import ClubIntelligence from "@/pages/club/ClubIntelligence";
import ClubPlayerDetail from "@/pages/club/ClubPlayerDetail";
import MatchReport from "@/pages/club/MatchReport";
import MatchHistory from "@/pages/club/MatchHistory";
import MatchReportDetail from "@/pages/club/MatchReportDetail";
import TeamOverview from "@/pages/club/TeamOverview";

// Protected Route Components
const CoachRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <LoadingScreen />;
  }
  
  if (!user || user.role !== 'coach') {
    return <Navigate to="/unified-login" replace />;
  }
  
  return children;
};

const ParentRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <LoadingScreen />;
  }
  
  if (!user || user.role === 'coach') {
    return <Navigate to="/unified-login" replace />;
  }
  
  return children;
};

const PlatformAdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <LoadingScreen />;
  }
  
  if (!user || user.role !== 'platform_admin') {
    return <Navigate to="/unified-login" replace />;
  }
  
  return children;
};

const LoadingScreen = () => (
  <div className="min-h-screen bg-background flex items-center justify-center">
    <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
  </div>
);

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ThemeProvider>
          <DemoProvider>
            <DemoBar />
            <Routes>
            {/* Public Routes */}
            <Route path="/" element={<SaaSLandingPage />} />
            <Route path="/demo" element={<DemoLandingPage />} />
            <Route path="/coach/signup" element={<CoachSignupPage />} />
            <Route path="/coach/login" element={<Navigate to="/unified-login" replace />} />
            <Route path="/login" element={<Navigate to="/unified-login" replace />} />
            <Route path="/unified-login" element={<UnifiedLoginPage />} />
            <Route path="/athlete/signup" element={<AthleteSignupPage />} />
            <Route path="/club/signup" element={<ClubSignupPage />} />
            <Route path="/club-pilot" element={<ClubPilotPage />} />
            <Route path="/get-started" element={<GetStartedPage />} />
            <Route path="/invite/:inviteId" element={<InviteSignupPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
            
            {/* Payment Routes */}
            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/payment/cancel" element={<PaymentCancel />} />
            <Route path="/pay/athlete-pass/:athleteId" element={<MockPaymentPage />} />
            
            {/* Coach Routes */}
            <Route path="/coach" element={
              <CoachRoute>
                <CoachLayout />
              </CoachRoute>
            }>
              <Route index element={<CoachDashboard />} />
              <Route path="athletes" element={<CoachAthletes />} />
              <Route path="athletes/:athleteId" element={<CoachAthleteDetail />} />
              <Route path="athletes/:athleteId/evaluation" element={<AthleteEvaluationPage />} />
              <Route path="sessions" element={<CoachSessions />} />
              <Route path="drills" element={<CoachDrills />} />
              <Route path="packages" element={<CoachPackages />} />
              <Route path="training-plans" element={<CoachTrainingPlans />} />
              <Route path="videos" element={<CoachVideos />} />
              <Route path="financials" element={<CoachFinancials />} />
              <Route path="vouchers" element={<CoachVouchers />} />
              <Route path="messages" element={<CoachMessages />} />
              <Route path="weekly-challenge" element={<CoachWeeklyChallenge />} />
              <Route path="calendar" element={<CalendarPage />} />
              <Route path="settings" element={<CoachSettings />} />
              <Route path="invited-athletes" element={<InvitedAthletes />} />
            </Route>

            {/* Athlete Workspace (Invited Coach - Limited Access) */}
            <Route path="/coach/athlete-workspace/:athleteId" element={
              <CoachRoute>
                <AthleteWorkspace />
              </CoachRoute>
            } />
            
            {/* Parent/Athlete Routes */}
            <Route path="/dashboard" element={
              <ParentRoute>
                <AthleteProvider>
                  <ParentLayout />
                </AthleteProvider>
              </ParentRoute>
            }>
              <Route index element={<ParentDashboard />} />
              <Route path="goals" element={<RequireAthletePass><PlayerGoals /></RequireAthletePass>} />
              <Route path="train-at-home" element={<RequireAthletePass><TrainAtHome /></RequireAthletePass>} />
              <Route path="train-at-home/guided" element={<RequireAthletePass><GuidedTraining /></RequireAthletePass>} />
              <Route path="messages" element={<RequireAthletePass><ParentMessages /></RequireAthletePass>} />
              <Route path="technical" element={<RequireAthletePass><ParentTechnicalDev /></RequireAthletePass>} />
              <Route path="development-profile" element={<RequireAthletePass><DevelopmentProfile /></RequireAthletePass>} />
              <Route path="weekly-challenge" element={<WeeklyChallenge />} />
              <Route path="videos" element={<RequireAthletePass><ParentVideos /></RequireAthletePass>} />
              <Route path="training-plan" element={<RequireAthletePass><ParentTrainingPlan /></RequireAthletePass>} />
              <Route path="sessions" element={<RequireAthletePass><ParentSessions /></RequireAthletePass>} />
              <Route path="monthly-report" element={<RequireAthletePass><ParentMonthlyReport /></RequireAthletePass>} />
              <Route path="calendar" element={<CalendarPage />} />
              <Route path="settings" element={<ParentSettings />} />
              {/* Team Context Routes */}
              <Route path="team/:teamId" element={<ParentTeamView />} />
              <Route path="team/:teamId/technical" element={<ParentTeamView section="technical" />} />
              <Route path="team/:teamId/training" element={<ParentTeamView section="training" />} />
              <Route path="team/:teamId/sessions" element={<ParentTeamView section="sessions" />} />
              <Route path="team/:teamId/videos" element={<ParentTeamView section="videos" />} />
            </Route>
            
            {/* Platform Admin Routes */}
            <Route path="/platform-admin/login" element={<Navigate to="/unified-login" replace />} />
            <Route path="/platform-admin" element={
              <PlatformAdminRoute>
                <PlatformAdminLayout />
              </PlatformAdminRoute>
            }>
              <Route index element={<PlatformAdminDashboard />} />
              <Route path="coaches" element={<PlatformAdminCoaches />} />
              <Route path="users" element={<PlatformAdminUsers />} />
              <Route path="billing" element={<PlatformAdminBilling />} />
              <Route path="analytics" element={<PlatformAdminAnalytics />} />
              <Route path="activation" element={<PlatformAdminActivation />} />
              <Route path="transitions" element={<PlatformAdminTransitions />} />
              <Route path="settings" element={<PlatformAdminSettings />} />
              <Route path="audit" element={<PlatformAdminAudit />} />
              <Route path="challenges" element={<PlatformAdminChallenges />} />
            </Route>
            
            {/* Team Workspace Routes (Club/Team Management) */}
            <Route path="/team/login" element={<Navigate to="/unified-login" replace />} />
            <Route path="/team/join/:inviteCode" element={<TeamJoinPage />} />
            <Route path="/team/player-join/:inviteCode" element={<PlayerJoinPage />} />
            <Route path="/team/:teamId" element={<TeamLayout />}>
              <Route index element={<TeamDashboard />} />
              <Route path="athletes" element={<TeamAthletes />} />
              <Route path="athletes/:athleteId" element={<TeamAthleteDetail />} />
              <Route path="sessions" element={<TeamSessions />} />
              <Route path="match-report" element={<TeamMatchReport />} />
              <Route path="schedule-match" element={<TeamScheduleMatch />} />
              <Route path="training-plans" element={<TeamTrainingPlans />} />
              <Route path="drills" element={<TeamDrills />} />
              <Route path="videos" element={<TeamVideos />} />
              <Route path="messages" element={<TeamMessages />} />
              <Route path="notifications" element={<TeamNotifications />} />
            </Route>
            
            {/* Club Admin Panel Routes */}
            <Route path="/club/login" element={<Navigate to="/unified-login" replace />} />
            <Route path="/club/:clubId" element={<ClubLayout />}>
              <Route index element={<ClubDashboard />} />
              <Route path="intelligence" element={<ClubIntelligence />} />
              <Route path="intelligence/player/:playerId" element={<ClubPlayerDetail />} />
              <Route path="match-report" element={<MatchReport />} />
              <Route path="match-history" element={<MatchHistory />} />
              <Route path="match-history/:reportId" element={<MatchReportDetail />} />
              <Route path="teams/:teamId" element={<TeamOverview />} />
              <Route path="teams" element={<ClubTeams />} />
              <Route path="coaches" element={<ClubCoaches />} />
              <Route path="managers" element={<ClubManagers />} />
              <Route path="athletes" element={<ClubAthletes />} />
              <Route path="sessions" element={<ClubSessions />} />
              <Route path="invites" element={<ClubInvites />} />
              <Route path="settings" element={<ClubSettings />} />
            </Route>
            
            {/* Catch all */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster position="top-right" />
          <HelpdeskWidget />
          <InstallPWAPrompt />
          </DemoProvider>
        </ThemeProvider>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
